// inspiration - https://codepen.io/xgad/post/svg-radial-progress-meters

import { Component } from 'react';
import cx from 'classnames';
import styled, { css, keyframes } from 'styled-components';
import voca from 'voca';
import { isNumber } from 'lodash';

const calcPercentage = (circumference, percentage) => circumference * (1 - percentage / 100);

const StyledProgressText = styled.div.withConfig({ shouldForwardProp: (prop) => !['fontSize', 'colorName'].includes(prop) }).attrs(({ colorName }) => ({
  className: `absolute top-0 left-0 w-100 h-100 flex items-center justify-center fw5 c-${colorName}`,
}))(({ fontSize }) => ({ fontSize }));

const StyledProgressSvg = styled.svg
  .withConfig({ shouldForwardProp: (prop) => prop !== 'colorName' })
  .attrs(({ colorName }) => ({ className: `ProgressWithPercentage db circle-${colorName}` }))({
  transform: 'rotate(-90deg)',
});

const StyledProgressCircle = styled.circle`
  ${({ animation }) => animation};
  stroke-dashoffset: ${({ endPoint }) => endPoint};
  stroke-width: ${({ strokeWidth }) => strokeWidth};
  stroke-dasharray: ${({ circumference }) => circumference};
`;

const ProgressWithPercentage = ({ size = 0, start = 0, end = 0, colorName, className }) => {
  const strokeWidth = Math.ceil(size / 8.3);
  const fontSize = Math.ceil(size / 4);
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const startPoint = calcPercentage(circumference, start);
  const endPoint = calcPercentage(circumference, end);
  const animationKeyframes = keyframes`
    from { 
      strokeDashoffset: ${startPoint};
    }
    to { 
      strokeDashoffset: ${endPoint};
    }
  `;
  const animation = css`
    animation: 200ms ${animationKeyframes} linear;
  `;

  return (
    <div className={cx('relative', className)} style={{ width: size, height: size }} data-test="progress-with-percentage">
      <StyledProgressSvg colorName={colorName} width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <StyledProgressCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          endPoint={endPoint}
          animation={animation}
          circumference={circumference}
          fill="none"
        />
      </StyledProgressSvg>
      {fontSize > 6 ? (
        <StyledProgressText colorName={colorName} fontSize={fontSize}>
          {end}%
        </StyledProgressText>
      ) : null}
    </div>
  );
};

const overlayStyle = `
        position:  absolute;
        z-index: 1;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
`;

const rotateAnimation = keyframes`
    from {
        transform: rotate(0)
    }
    to {
        transform: rotate(360deg)
    }
`;

const StyledInfiniteProgress = styled.div`
  ${({ size }) =>
    isNumber(size)
      ? css`
          width: ${size}px;
          height: ${size}px;
        `
      : !size
        ? css`
            width: 100%;
            height: 100%;
          `
        : null}

  ${({ overlay }) =>
    overlay
      ? css`
          ${overlayStyle}
        `
      : ''};

  animation: ${rotateAnimation} 1s infinite linear;
`;

const InfiniteProgress = ({ size, colorName, className, overlay = false }) => (
  <StyledInfiniteProgress
    className={`InfiniteProgress db circle-${colorName} ${voca.isString(size) && size ? `box-${size}` : ''} ${className || ''}`}
    colorName={colorName}
    size={size}
    overlay={overlay}
    data-test="infinite-progress"
  >
    <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
      <circle cx="50%" cy="50%" fill="none" r="42" strokeDasharray="197.92033717615698 67.97344572538566" strokeWidth="13" />
    </svg>
  </StyledInfiniteProgress>
);

class Progress extends Component {
  static defaultProps = {
    colorName: 'in-progress',
    percentage: -1,
    overlay: false,
    size: 0,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.percentage !== prevState.currPercentage) {
      return {
        prevPercentage: prevState.currPercentage,
        currPercentage: nextProps.percentage,
      };
    }

    return null;
  }

  state = {
    prevPercentage: 0,
    currPercentage: 0,
  };

  render() {
    const showPercentage = this.props.percentage >= 0 && isNumber(this.props.size);
    const size = isNumber(this.props.size) ? this.props.size : 0;

    return (
      <>
        {showPercentage ? (
          <ProgressWithPercentage
            className={this.props.className}
            colorName={this.props.colorName}
            size={size}
            start={this.state.prevPercentage}
            end={this.state.currPercentage}
          />
        ) : null}

        {!showPercentage ? (
          <InfiniteProgress colorName={this.props.colorName} size={this.props.size} className={this.props.className} overlay={this.props.overlay} />
        ) : null}
      </>
    );
  }
}

export default Progress;
